import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, Download } from "lucide-react";
import { Reveal } from "./Reveal";
import { CompareTray, CompareModal } from "./GradeCompare";
import { openGradeFinder } from "./GradeFinder";
import { useLang, usePath } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_FILTERS = ["All", "Light Brown", "Brown", "Dark Brown", "Black"];
const PH_FILTERS = ["All", "5.0 - 6.9", "7.0 - 8.0", "9.0 - 11.0"];
const TYPE_FILTERS = ["All", "Natural", "Alkalized"];
const APP_FILTERS = ["All", "Bakery", "Beverages", "Confectionery", "Dairy", "Ice Cream", "Chocolate Coatings"];

const inPhBucket = (ph, bucket) => {
  if (bucket === "All") return true;
  const start = parseFloat(ph);
  if (bucket === "5.0 - 6.9") return start >= 5 && start < 7;
  if (bucket === "7.0 - 8.0") return start >= 7 && start < 9;
  if (bucket === "9.0 - 11.0") return start >= 9;
  return true;
};

const FilterGroup = ({ label, options, value, onChange, renderLabel, testidPrefix }) => (
  <div>
    <span className="font-data text-[10px] tracking-[0.2em] uppercase text-clara-darkmuted block mb-3">{label}</span>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          data-testid={`${testidPrefix}-${o.replace(/[\s.]+/g, "-").toLowerCase()}`}
          className={`px-4 py-1.5 text-xs font-medium rounded-full border transition-colors ${
            value === o
              ? "bg-clara-terracotta border-clara-terracotta text-clara-cream"
              : "border-clara-cream/25 text-clara-cream/70 hover:border-clara-cream"
          }`}
        >
          {renderLabel(o)}
        </button>
      ))}
    </div>
  </div>
);

export const ProductRange = ({ h1 = false, simple = false }) => {
  const { t } = useLang();
  const lp = usePath();
  const navigate = useNavigate();
  const H = h1 ? "h1" : "h2";
  const P = t.products;
  const [products, setProducts] = useState([]);
  const [colorF, setColorF] = useState("All");
  const [phF, setPhF] = useState("All");
  const [typeF, setTypeF] = useState("All");
  const [appF, setAppF] = useState("All");
  const [compareSel, setCompareSel] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const toggleCompare = (code) =>
    setCompareSel((sel) =>
      sel.includes(code) ? sel.filter((c) => c !== code) : sel.length < 3 ? [...sel, code] : sel
    );

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () =>
      products.filter(
        (p) =>
          (colorF === "All" || p.color === colorF) &&
          inPhBucket(p.ph, phF) &&
          (typeF === "All" || p.type === typeF) &&
          (appF === "All" || (p.applications || []).includes(appF))
      ),
    [products, colorF, phF, typeF, appF]
  );

  const openGrade = (code) => navigate(lp(`/grades/${code}`));

  return (
    <section id="products" data-testid="products-section" className="relative bg-clara-espresso text-clara-cream py-24 lg:py-36 noise-overlay">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="eyebrow text-clara-terracotta mb-6">{P.eyebrow}</p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <H className="font-display font-light text-5xl lg:text-8xl leading-[0.9] tracking-tighter">
              {P.headingA} <br /><span className="italic text-clara-terracotta">{P.headingEm}</span>
            </H>
            <p className="max-w-sm text-clara-darkmuted leading-relaxed">
              {P.intro}
            </p>
          </div>
        </Reveal>

        {!simple && (
          <div className="flex flex-wrap gap-3 mb-10">
            <button onClick={openGradeFinder} data-testid="finder-open-grades" className="inline-flex items-center gap-2 rounded-full border border-clara-terracotta/60 px-6 py-3 text-sm font-semibold text-clara-cream hover:bg-clara-terracotta transition-colors duration-300">
              {t.finder.title}
              <ArrowUpRight className="w-4 h-4" />
            </button>
            <a href="/clara-catalogue.pdf" download data-testid="grades-catalogue-btn" className="inline-flex items-center gap-2 rounded-full border border-clara-cream/30 px-6 py-3 text-sm font-semibold text-clara-cream hover:border-clara-cream transition-colors duration-300">
              <Download className="w-4 h-4" /> {t.catalogueCta}
            </a>
          </div>
        )}

        {/* Filters (full version only — homepage grid stays simple) */}
        {!simple && (
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-8 mb-12 border-y border-clara-cream/15 py-6" data-testid="product-filters">
          <FilterGroup label={P.colourLabel} options={COLOR_FILTERS} value={colorF} onChange={setColorF} renderLabel={(c) => P.colours[c]} testidPrefix="filter-color" />
          <FilterGroup label={P.phLabel} options={PH_FILTERS} value={phF} onChange={setPhF} renderLabel={(c) => (c === "All" ? P.colours.All : c)} testidPrefix="filter-ph" />
          <FilterGroup label={P.typeLabel} options={TYPE_FILTERS} value={typeF} onChange={setTypeF} renderLabel={(c) => P.types[c]} testidPrefix="filter-type" />
          <FilterGroup label={P.appLabel} options={APP_FILTERS} value={appF} onChange={setAppF} renderLabel={(c) => P.appLabels[c]} testidPrefix="filter-app" />
        </div>
        )}

        {/* Grid */}
        <motion.div layout className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6" data-testid="products-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.div
                layout key={p.code}
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                onClick={() => openGrade(p.code)}
                data-testid={`product-card-${p.code}`}
                className="group relative text-left overflow-hidden border border-clara-cream/15 aspect-[4/5] cursor-pointer"
                style={{ backgroundColor: p.hex }}
              >
                <img alt={`Clara ${p.code} cocoa powder`} loading="lazy" decoding="async" src={p.image} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 font-data text-[10px] tracking-[0.15em] text-clara-cream/80 uppercase">{p.type}</div>
                {!simple && (
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleCompare(p.code); }}
                    data-testid={`compare-check-${p.code}`}
                    aria-pressed={compareSel.includes(p.code)}
                    aria-label={`${t.compare.select} ${p.code}`}
                    className={`absolute top-3 right-3 w-7 h-7 rounded-full border flex items-center justify-center transition-colors ${
                      compareSel.includes(p.code)
                        ? "bg-clara-terracotta border-clara-terracotta text-clara-cream"
                        : "border-clara-cream/50 text-transparent hover:border-clara-cream bg-clara-espresso/40 backdrop-blur-sm"
                    }`}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                )}
                <div className={`absolute ${simple ? "bottom-4" : "bottom-14"} left-4 right-4`}>
                  <div className="font-display text-3xl text-clara-cream">{p.code}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-clara-cream/80">{P.colours[p.color]}</span>
                    <span className="font-data text-xs text-clara-terracotta">pH {p.ph}</span>
                  </div>
                </div>
                {!simple && (
                <div
                  data-testid={`view-tds-${p.code}`}
                  className="absolute bottom-0 inset-x-0 flex items-center justify-between gap-2 bg-clara-cream text-clara-espresso px-4 py-3 font-data text-[10px] tracking-[0.12em] uppercase group-hover:bg-clara-terracotta group-hover:text-clara-cream transition-colors duration-300"
                >
                  {P.viewSpecsCta}
                  <ArrowUpRight className="w-3.5 h-3.5 shrink-0" />
                </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {filtered.length === 0 && products.length > 0 && (
          <p className="text-clara-darkmuted font-data text-sm mt-10">{P.noMatch}</p>
        )}
      </div>

      {!simple && (
        <>
          <CompareTray
            selected={compareSel}
            onOpen={() => setCompareOpen(true)}
            onClear={() => setCompareSel([])}
            onRemove={(code) => setCompareSel((sel) => sel.filter((c) => c !== code))}
          />
          <CompareModal selected={compareSel} products={products} open={compareOpen} onClose={() => setCompareOpen(false)} />
        </>
      )}
    </section>
  );
};
